<template>
  <AuthShell
    title="Choose Username"
    subtitle="Finish creating your Filmsomniac account."
    :show-hero="false"
  >
    <div class="auth-panel__section">
      <h2 class="auth-panel__title">Choose your username</h2>
      <p class="auth-panel__helper" data-testid="google-signup-email">
        {{ email ? `Google account: ${email}` : 'Finish your Google signup.' }}
      </p>
    </div>

    <div v-if="errorMessage" class="auth-panel__section">
      <div class="auth-error-message" data-testid="google-signup-error">{{ errorMessage }}</div>
    </div>

    <form class="auth-panel__section auth-form" @submit.prevent="onSubmit">
      <div class="auth-field">
        <label class="auth-label" for="google-signup-username">Username</label>
        <input
          id="google-signup-username"
          v-model="username"
          class="auth-input"
          data-testid="google-signup-username"
          type="text"
          required
          maxlength="150"
          autocomplete="username"
          autofocus
        >
      </div>

      <div class="auth-actions">
        <button
          class="auth-submit"
          data-testid="google-signup-submit"
          type="submit"
          :disabled="isSubmitting || isLoading"
        >
          {{ isSubmitting ? 'Creating account...' : 'Create account' }}
        </button>
      </div>
    </form>
  </AuthShell>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import AuthShell from '../components/AuthShell.vue'
import { useAuthStore } from '../stores/auth'
import { safeRedirectPath } from '../utils/auth'

const auth = useAuthStore()
const router = useRouter()
const email = ref('')
const username = ref('')
const redirectPath = ref('/home')
const errorMessage = ref('')
const isLoading = ref(true)
const isSubmitting = ref(false)

const loadPendingSignup = async () => {
  try {
    const response = await fetch('/api/v1/auth/google/signup', {
      credentials: 'same-origin',
    })
    if (!response.ok) {
      throw new Error('No pending Google signup.')
    }
    const data = await response.json()
    email.value = data.email || ''
    redirectPath.value = safeRedirectPath(data.redirect, '/home')
  } catch {
    errorMessage.value = 'Google signup was not found. Please try again.'
  } finally {
    isLoading.value = false
  }
}

const onSubmit = async () => {
  errorMessage.value = ''
  isSubmitting.value = true
  try {
    const response = await fetch('/api/v1/auth/google/signup', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: username.value }),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      const firstError = typeof data === 'object' ? Object.values(data)[0] : null
      errorMessage.value = Array.isArray(firstError)
        ? firstError[0]
        : firstError || 'Google signup failed.'
      return
    }
    const data = await response.json()
    auth.setTokens(data.access, data.refresh, data.username)
    await router.replace(safeRedirectPath(data.redirect, redirectPath.value))
  } catch {
    errorMessage.value = 'Network error. Please try again.'
  } finally {
    isSubmitting.value = false
  }
}

onMounted(loadPendingSignup)
</script>
